import React, { useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Stack from '@mui/joy/Stack';
import Textarea from '@mui/joy/Textarea';
import Typography from '@mui/joy/Typography';
import Alert from '@mui/joy/Alert';
import Chip from '@mui/joy/Chip';
import LinearProgress from '@mui/joy/LinearProgress';

import FlowIcon from '@mui/icons-material/AccountTree';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import ColorPicker from '../forms/ColorPicker';
import UrlNameInput from '../forms/UrlNameInput';

import { themeColors, shadows } from '../../styles/theme';

const STEPS = [
  {
    id: 'basic',
    label: 'Basic Information',
    description: 'Name your flow and set the URL',
    icon: FlowIcon
  },
  {
    id: 'style',
    label: 'Branding',
    description: 'Choose your theme color',
    icon: InfoOutlinedIcon
  },
  {
    id: 'review',
    label: 'Review & Create',
    description: 'Confirm your settings',
    icon: CheckCircleIcon
  }
];

export default function ModernCreateProjectModal({
  open,
  onClose,
  onSubmit,
  loading = false,
  error = null,
  workspace = null
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    url_name: '',
    description: '',
    main_color: themeColors.primary[500]
  });
  const [urlError, setUrlError] = useState('');

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'url_name') {
      setUrlError(!value ? 'URL name is required' : '');
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.name || !formData.url_name) return;
    }

    if (activeStep < STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(formData);
    handleReset();
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      name: '',
      url_name: '',
      description: '',
      main_color: themeColors.primary[500]
    });
    setUrlError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return formData.name && formData.url_name && !urlError;
      case 1:
        return true;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const progress = ((activeStep + 1) / STEPS.length) * 100;

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3}>
            <FormControl required>
              <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
                Flow Name
              </FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g., Customer Support Flow"
                autoFocus
                sx={{
                  border: 'none',
                  boxShadow: shadows.sm,
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '--Input-focusedThickness': '0px',
                  '&:focus-within': {
                    boxShadow: shadows.focus,
                  },
                  transition: 'all 0.2s ease',
                  fontSize: '1rem',
                  py: 1.5,
                }}
              />
            </FormControl>

            <UrlNameInput
              value={formData.url_name}
              onChange={(value) => updateField('url_name', value)}
              label="URL Name"
              required
              error={urlError}
              placeholder="customer-support"
              helperText="This will be part of your flow's URL"
            />

            <FormControl>
              <FormLabel sx={{ fontWeight: 600, mb: 1, color: themeColors.neutral[800] }}>
                Description
                <Typography component="span" level="body-sm" sx={{ ml: 1, color: themeColors.neutral[500], fontWeight: 400 }}>
                  (Optional)
                </Typography>
              </FormLabel>
              <Textarea
                minRows={3}
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Brief description of what this flow does..."
                sx={{
                  border: 'none',
                  boxShadow: shadows.sm,
                  borderRadius: 2,
                  backgroundColor: 'white',
                  '--Textarea-focusedThickness': '0px',
                  '&:focus-within': {
                    boxShadow: shadows.focus,
                  },
                  transition: 'all 0.2s ease',
                  fontSize: '0.95rem',
                }}
              />
            </FormControl>
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            <ColorPicker
              value={formData.main_color}
              onChange={(color) => updateField('main_color', color)}
              label="Theme Color"
              helperText="This color will be used for branding throughout your flow"
              required
            />

            <Box
              sx={{
                p: 3,
                backgroundColor: themeColors.neutral[50],
                borderRadius: 2.5,
                border: 'none',
                boxShadow: shadows.inner,
              }}
            >
              <Typography level="body-sm" sx={{ mb: 2, fontWeight: 600, color: themeColors.neutral[700] }}>
                Preview
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${formData.main_color}, ${formData.main_color}DD)`,
                      boxShadow: `0 4px 12px ${formData.main_color}40`,
                    }}
                  />
                  <Box>
                    <Typography level="body-sm" sx={{ color: themeColors.neutral[600] }}>
                      Primary Color
                    </Typography>
                    <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: themeColors.neutral[500] }}>
                      {formData.main_color}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="sm"
                    sx={{
                      backgroundColor: `${formData.main_color}15`,
                      color: formData.main_color,
                      border: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Sample Chip
                  </Chip>
                  <Button
                    size="sm"
                    sx={{
                      backgroundColor: formData.main_color,
                      '&:hover': {
                        backgroundColor: `${formData.main_color}DD`,
                      },
                    }}
                  >
                    Sample Button
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={3}>
            <Box
              sx={{
                p: 4,
                backgroundColor: 'white',
                borderRadius: 2.5,
                border: 'none',
                boxShadow: shadows.md,
              }}
            >
              <Stack spacing={3}>
                {/* Flow Name */}
                <Box>
                  <Typography level="body-xs" sx={{ color: themeColors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontWeight: 700 }}>
                    Flow Name
                  </Typography>
                  <Typography level="h4" sx={{ color: formData.main_color, fontWeight: 700 }}>
                    {formData.name}
                  </Typography>
                </Box>

                {/* URL */}
                <Box>
                  <Typography level="body-xs" sx={{ color: themeColors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontWeight: 700 }}>
                    URL Path
                  </Typography>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: themeColors.neutral[100],
                      px: 2,
                      py: 1,
                      borderRadius: 1.5,
                    }}
                  >
                    <Typography sx={{ fontFamily: 'monospace', color: themeColors.neutral[700], fontWeight: 600 }}>
                      /{workspace?.url_name}/{formData.url_name}
                    </Typography>
                  </Box>
                </Box>

                {/* Description */}
                {formData.description && (
                  <Box>
                    <Typography level="body-xs" sx={{ color: themeColors.neutral[500], textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontWeight: 700 }}>
                      Description
                    </Typography>
                    <Typography level="body-md" sx={{ color: themeColors.neutral[700] }}>
                      {formData.description}
                    </Typography>
                  </Box>
                )}

                {/* Workspace & Color */}
                <Box sx={{ display: 'flex', gap: 3, pt: 2, borderTop: `1px solid ${themeColors.neutral[100]}` }}>
                  <Box>
                    <Typography level="body-xs" sx={{ color: themeColors.neutral[500], mb: 0.5 }}>
                      Workspace
                    </Typography>
                    <Chip size="sm" variant="soft" color="primary">
                      {workspace?.name || 'Unknown'}
                    </Chip>
                  </Box>

                  <Box>
                    <Typography level="body-xs" sx={{ color: themeColors.neutral[500], mb: 0.5 }}>
                      Theme Color
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          backgroundColor: formData.main_color,
                          boxShadow: shadows.sm,
                        }}
                      />
                      <Typography level="body-sm" sx={{ fontFamily: 'monospace', color: themeColors.neutral[700] }}>
                        {formData.main_color}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Alert
              variant="soft"
              color="success"
              sx={{
                borderRadius: 2,
                border: 'none',
                backgroundColor: `${themeColors.success}10`,
              }}
            >
              <Typography level="body-sm">
                Everything looks good! Click "Create Flow" to get started.
              </Typography>
            </Alert>
          </Stack>
        );

      default:
        return null;
    }
  };

  const StepIcon = STEPS[activeStep].icon;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          width: 650,
          maxHeight: '90vh',
          overflow: 'auto',
          border: 'none',
          boxShadow: shadows.xl,
          borderRadius: 3,
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 4,
            pb: 3,
            background: `linear-gradient(135deg, ${themeColors.primary[500]}08, ${themeColors.primary[500]}03)`,
            borderBottom: `1px solid ${themeColors.neutral[100]}`,
            position: 'relative',
          }}
        >
          <ModalClose
            sx={{
              top: 16,
              right: 16,
              backgroundColor: 'white',
              boxShadow: shadows.sm,
              '&:hover': {
                backgroundColor: themeColors.neutral[100],
              },
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 8px 16px ${themeColors.primary[500]}30`,
              }}
            >
              <StepIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography level="h3" sx={{ mb: 0.5, fontWeight: 700, color: themeColors.neutral[900] }}>
                {STEPS[activeStep].label}
              </Typography>
              <Typography level="body-sm" sx={{ color: themeColors.neutral[600] }}>
                {STEPS[activeStep].description}
              </Typography>
              {workspace && (
                <Chip size="sm" variant="soft" sx={{ mt: 1.5 }}>
                  {workspace.name}
                </Chip>
              )}
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600], fontWeight: 600 }}>
                Step {activeStep + 1} of {STEPS.length}
              </Typography>
              <Typography level="body-xs" sx={{ color: themeColors.neutral[600], fontWeight: 600 }}>
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              determinate
              value={progress}
              sx={{
                backgroundColor: themeColors.neutral[200],
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                },
                height: 6,
                borderRadius: 3,
              }}
            />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4 }}>
          {error && (
            <Alert
              color="danger"
              sx={{
                mb: 3,
                borderRadius: 2,
                border: 'none',
                boxShadow: shadows.sm,
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ minHeight: 280, mb: 4 }}>
              {renderStepContent()}
            </Box>

            {/* Footer Actions */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                pt: 3,
                borderTop: `1px solid ${themeColors.neutral[100]}`,
              }}
            >
              <Button
                variant="plain"
                onClick={handleBack}
                disabled={activeStep === 0}
                startDecorator={<ArrowBackIcon />}
                sx={{
                  visibility: activeStep === 0 ? 'hidden' : 'visible',
                  color: themeColors.neutral[700],
                  '&:hover': {
                    backgroundColor: themeColors.neutral[100],
                  },
                }}
              >
                Back
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {activeStep === STEPS.length - 1 ? (
                  <Button
                    type="submit"
                    loading={loading}
                    disabled={!canProceed()}
                    startDecorator={<CheckCircleIcon />}
                    size="lg"
                    sx={{
                      background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                      boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
                      fontWeight: 600,
                      px: 4,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 16px ${themeColors.primary[500]}50`,
                      },
                      '&:disabled': {
                        opacity: 0.5,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Create Flow
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    endDecorator={<ArrowForwardIcon />}
                    size="lg"
                    sx={{
                      background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                      boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
                      fontWeight: 600,
                      px: 4,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 16px ${themeColors.primary[500]}50`,
                      },
                      '&:disabled': {
                        opacity: 0.5,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Continue
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
